import { useState, useRef, useEffect } from "react";
import { TableColumn } from "react-data-table-component";
import { Tab } from "react-bootstrap";
import { Page } from "containers/Page";
import { useEmpresas } from "hooks/useEmpresas";
import { useDivisoes } from "hooks/useDivisoes";
import { LoadingScreen } from "components/LoadingScreen";
import { toastErro, toastSucesso } from "components/Toast";
import { FiltrosPagarIpva } from "./containers/Filtros";
import { ListaDebitos } from "./containers/ListaDebitos";
import { ModalResultadoPagamento } from "./containers/ModalResultadoPagamento";
import { ModalBaixaManual } from "components/ModalBaixaManual";
import { ModalConfirmarLote } from "./containers/ModalConfirmarLote";
import { usePagarIpva } from "./hooks/usePagarIpva";
import { IDebitoIpva, IResultadoPagamento } from "./interfaces/IDebitoIpva";
import { IFiltroDebitoIpva } from "./interfaces/IFiltroDebitoIpva";
import { Container, CustomTabs } from "./style";

type Aba = "aguardando" | "pago";

const STATUS_ABA: Record<Aba, number[]> = {
  aguardando: [0, 3, 4],
  pago: [1, 2],
};

export function PagarIpva() {
  const { empresasOptions } = useEmpresas();
  const [empresaSelecionada, setEmpresaSelecionada] = useState<
    number | undefined
  >();
  const { divisoesOptions, listarTodasDivisoes } = useDivisoes("");

  const {
    loading,
    setLoading,
    debitos,
    setDebitos,
    totalRows,
    setTotalRows,
    page,
    setPage,
    limit,
    setLimit,
    listarDebitos,
    pagarIpva,
    salvarPagamentoManual,
    cancelarDebito,
    baixarComprovante,
    exportarDebitos,
    validarVeiculos,
    validarProcessos,
    onClickProcesso,
    loadingExportar,
  } = usePagarIpva();

  const montado = useRef(false);
  useEffect(() => {
    if (montado.current) return;
    montado.current = true;
    listarDebitos({ statusesPagamento: STATUS_ABA["aguardando"] }, 1, limit);
  }, []);

  const [aba, setAba] = useState<Aba>("aguardando");
  const [filtrosAtivos, setFiltrosAtivos] = useState<IFiltroDebitoIpva>({
    statusesPagamento: STATUS_ABA["aguardando"],
  });

  const [selectedRows, setSelectedRows] = useState<IDebitoIpva[]>([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [pagandoId, setPagandoId] = useState<string | null>(null);

  const [showModalResultado, setShowModalResultado] = useState(false);
  const [resultados, setResultados] = useState<IResultadoPagamento[]>([]);

  const [showModalPagamentoManual, setShowModalPagamentoManual] =
    useState(false);
  const [debitoManual, setDebitoManual] = useState<IDebitoIpva | null>(null);

  const [showModalConfirmarLote, setShowModalConfirmarLote] = useState(false);

  const handleChangeAba = (novaAba: string | null) => {
    if (!novaAba || novaAba === aba) return;
    const abaAtualizada = novaAba as Aba;
    setAba(abaAtualizada);
    setDebitos([]);
    setTotalRows(0);
    setPage(1);
    setSelectedRows([]);
    setClearSelectedRows(false);
    const novosFiltros = { statusesPagamento: STATUS_ABA[abaAtualizada] };
    setFiltrosAtivos(novosFiltros);
    listarDebitos(novosFiltros, 1, limit);
  };

  const onChangeEmpresa = (valor: number) => {
    setEmpresaSelecionada(valor || undefined);
    listarTodasDivisoes(valor);
  };

  const handleBuscar = async (filtros: IFiltroDebitoIpva) => {
    const statusesPagamento =
      filtros.statusPagamento != null
        ? [filtros.statusPagamento]
        : STATUS_ABA[aba];
    const filtrosComStatus = { ...filtros, statusesPagamento };
    setFiltrosAtivos(filtrosComStatus);
    const { hasErro, data } = await listarDebitos(filtrosComStatus, 1, limit);
    if (hasErro) {
      toastErro(data?.message ?? "Erro ao buscar débitos");
      return;
    }
    setPage(1);
    setClearSelectedRows(true);
    setSelectedRows([]);
  };

  const handleSelectRows = (rows: IDebitoIpva[]) => {
    setClearSelectedRows(false);
    setSelectedRows(rows);
  };

  const handlePageChange = async (newPage: number) => {
    if (newPage === page) return;
    setPage(newPage);
    await listarDebitos(filtrosAtivos, newPage, limit);
  };

  const handleLimitChange = async (newLimit: number) => {
    if (newLimit === limit) return;
    setLimit(newLimit);
    await listarDebitos(filtrosAtivos, 1, newLimit);
    setPage(1);
  };

  const handleOnSort = (coluna: TableColumn<any>, direcao: string) => {
    listarDebitos(filtrosAtivos, 1, limit, coluna.sortField, direcao, false);
  };

  const handlePagarIndividual = async (id: number, exercicio: number) => {
    const chave = `${id}_${exercicio}`;
    setPagandoId(chave);
    const { hasErro, data } = await pagarIpva([{ idProcesso: id, exercicio }]);
    setPagandoId(null);
    if (hasErro) {
      toastErro(data?.message ?? "Erro ao pagar IPVA");
      return;
    }
    const resultado = Array.isArray(data) ? data[0] : null;
    if (resultado?.erro) {
      toastErro(resultado.mensagem ?? "Erro ao pagar IPVA");
      return;
    }
    setDebitos((prev) =>
      prev.filter((d) => !(d.id === id && d.exercicio === exercicio)),
    );
    toastSucesso("IPVA pago com sucesso!");
  };

  const handlePagarManual = (debito: IDebitoIpva) => {
    setDebitoManual(debito);
    setShowModalPagamentoManual(true);
  };

  const handleCancelarDebito = async (observacao: string) => {
    if (!debitoManual) return;
    setLoading(true);
    const { hasErro, data } = await cancelarDebito(
      debitoManual.id,
      debitoManual.exercicio,
      observacao,
    );
    setLoading(false);
    if (hasErro) {
      toastErro(data?.message ?? "Erro ao cancelar débito");
      return;
    }
    toastSucesso("Débito cancelado com sucesso!");
    await listarDebitos(filtrosAtivos, page, limit);
  };

  const handleConfirmarPagamentoManual = async (
    dataPagamento: string,
    comprovante: File,
    observacao: string,
    valorPago: number,
  ) => {
    if (!debitoManual) return;
    setLoading(true);
    const { hasErro, data } = await salvarPagamentoManual(
      debitoManual.id,
      debitoManual.exercicio,
      dataPagamento,
      comprovante,
      observacao,
      valorPago,
    );
    setLoading(false);
    if (hasErro) {
      toastErro(data?.message ?? "Erro ao salvar pagamento manual");
      return;
    }
    toastSucesso("Pagamento manual registrado com sucesso!");
    await listarDebitos(filtrosAtivos, page, limit);
  };

  const handleBaixarComprovante = async (id: number, exercicio: number) => {
    await baixarComprovante(id, exercicio);
  };

  const handlePagarEmLote = () => {
    if (selectedRows.length < 2) return;
    setShowModalConfirmarLote(true);
  };

  const handleConfirmarLote = async () => {
    setShowModalConfirmarLote(false);
    setLoading(true);
    const processos = selectedRows.map(({ id, exercicio }) => ({
      idProcesso: id,
      exercicio,
    }));
    const { hasErro, data } = await pagarIpva(processos);
    setLoading(false);

    if (hasErro) {
      toastErro(data?.message ?? "Erro ao realizar pagamentos");
      return;
    }

    const resultadosPagamento: IResultadoPagamento[] = (data ?? []).map(
      (r: any) => ({
        id: r.idProcesso,
        exercicio: r.exercicio,
        erro: r.erro,
        mensagem: r.mensagem,
      }),
    );

    setResultados(resultadosPagamento);
    setShowModalResultado(true);

    const idsPagos = new Set(
      resultadosPagamento
        .filter((r) => !r.erro)
        .map((r) => `${r.id}_${r.exercicio}`),
    );
    setDebitos((prev) =>
      prev.filter((d) => !idsPagos.has(`${d.id}_${d.exercicio}`)),
    );
    setClearSelectedRows(true);
    setSelectedRows([]);
  };

  const filtrosComuns = {
    empresasOptions,
    divisoesOptions,
    empresaSelecionada,
    onChangeEmpresa,
    onSubmit: handleBuscar,
    validarVeiculos,
    validarProcessos,
  };

  const listaComum = {
    debitos,
    loading,
    totalRows,
    limit,
    filtros: filtrosAtivos,
    onClickProcesso,
    onBaixarComprovante: handleBaixarComprovante,
    onPageChange: handlePageChange,
    onLimitChange: handleLimitChange,
    exportarDebitos,
    onSort: handleOnSort,
  };

  return (
    <Page title="Pagar IPVA">
      <LoadingScreen mostrar={loading} />
      <Container>
        <CustomTabs activeKey={aba} onSelect={handleChangeAba}>
          <Tab eventKey="aguardando" title="Aguardando pagamento">
            <FiltrosPagarIpva key={aba} modo="aguardando" {...filtrosComuns} />
            <ListaDebitos
              modo="aguardando"
              selectedRows={selectedRows}
              clearSelectedRows={clearSelectedRows}
              pagandoId={pagandoId}
              onSelectRows={handleSelectRows}
              onPagarIndividual={handlePagarIndividual}
              onPagarManual={handlePagarManual}
              onPagarEmLote={handlePagarEmLote}
              loadingExportar={loadingExportar}
              {...listaComum}
            />
          </Tab>
          <Tab eventKey="pago" title="Itens pagos">
            <FiltrosPagarIpva key={aba} modo="pago" {...filtrosComuns} />
            <ListaDebitos
              loadingExportar={loadingExportar}
              modo="pago"
              {...listaComum}
            />
          </Tab>
        </CustomTabs>
      </Container>
      <ModalBaixaManual
        show={showModalPagamentoManual}
        titulo={`Pagamento Manual – Processo ${debitoManual?.id ?? ""} / ${debitoManual?.exercicio ?? ""}`}
        handleClose={() => setShowModalPagamentoManual(false)}
        onConfirmarManual={handleConfirmarPagamentoManual}
        onCancelar={handleCancelarDebito}
      />
      <ModalConfirmarLote
        show={showModalConfirmarLote}
        selectedRows={selectedRows}
        handleClose={() => setShowModalConfirmarLote(false)}
        onConfirm={handleConfirmarLote}
      />
      <ModalResultadoPagamento
        show={showModalResultado}
        handleClose={() => setShowModalResultado(false)}
        resultados={resultados}
      />
    </Page>
  );
}
